import type { DrivingLicenceType } from '@eliadmin/core/src/api/jobportal/create-curriculum-api';
import { Field } from '@eliadmin/core/src/components/jobportal/jobportal-create-curriculum/jobportal-create-curriculum-form-fields';
import { Card, CardContent, CardHeader } from '@eliadmin/core/src/components/ui/card';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CreateCurriculumSchema } from '@eliadmin/core/src/pages/(jobportal)/create-curriculum/page';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useFormContext } from 'react-hook-form';

type JobportalDrivingLicencesCardCardProps = {
  className?: {
    wrapper?: string;
  };
  drivingLicences: DrivingLicenceType[];
};

export const JobportalDrivingLicencesCard = ({
  className,
  drivingLicences,
}: JobportalDrivingLicencesCardCardProps) => {
  const { t } = useTranslations();
  const form = useFormContext<CreateCurriculumSchema>();

  const hasDrivingLicence = form.watch('hasDrivingLicence');

  const formDrivingLicences = form.watch('driving_licences');

  const isChecked = (id: number) => {
    return !!formDrivingLicences?.find(el => el.id === id);
  };

  const handleDrivingLicenceCheckbox = (id: number) => {
    const driving_licences = [...(formDrivingLicences || [])];

    const index = driving_licences.findIndex(el => el.id === id);

    if (index !== -1) {
      driving_licences.splice(index, 1);
    } else {
      driving_licences.push({ id });
    }

    form.setValue('driving_licences', driving_licences);
  };

  return (
    <Card
      variant={'outlined'}
      className={cn('grid grid-cols-8', 'ea-jobportal-driving-licence-card', className?.wrapper)}
    >
      <div className="col-span-8 flex h-full justify-start pl-4 pt-4 md:col-span-1 md:items-start md:justify-center md:py-10">
        <Field type={'switch'} control={form.control} name="hasDrivingLicence" />
      </div>
      <div className="col-span-7">
        <CardHeader className={cn('pb-0', 'ea-jobportal-driving-licence-card__header')}>
          <h3
            className={cn(
              'text-lg font-semibold',
              'ea-jobportal-driving-licence-card__header-title',
            )}
          >
            {t('text909')}
          </h3>
          <p
            className={cn(
              'text-muted-foreground',
              'ea-jobportal-driving-licence-card__header-description',
            )}
          >
            {t('text910')}
          </p>
        </CardHeader>
        <CardContent className={cn('', 'ea-jobportal-driving-licence-card__content')}>
          {hasDrivingLicence && (
            <div
              className={cn(
                'mt-6 flex flex-wrap gap-6',
                'ea-jobportal-driving-licence__checkboxes-wrapper',
              )}
            >
              {drivingLicences.map(licence => (
                <div
                  key={licence.id}
                  className={cn(
                    'flex min-w-16 gap-2',
                    'ea-jobportal-driving-licence__checkbox-wrapper',
                  )}
                >
                  <Checkbox
                    value={licence.id}
                    checked={isChecked(licence.id)}
                    onCheckedChange={() => handleDrivingLicenceCheckbox(licence.id)}
                    className={cn('rounded-[4px]', 'ea-jobportal-driving-licence__checkbox')}
                  />
                  <p
                    className={cn(
                      'font-semibold text-primary',
                      'ea-jobportal-driving-licence__checkbox-label',
                    )}
                  >
                    {licence.name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
};
